/**
 * An IRRM2 engine run (PROD-2217, Emport 127): the API's record of one `Irrm2Submission` sent to Joe's
 * engine, from the browser's upload to the result stored in Blob.
 *
 * One `emp.EmpyreanEngineRuns` row per `emp.EmpyreanRuns` row whose `RunKind` is `IRRM2`. The run's own
 * `Status` stays coarse (`PENDING` until the engine side ends, then `COMPLETE` or `FAILED`); the engine
 * row says where the engine side is.
 */

/**
 * What a run produced: an Empyrean workbook (every run before PROD-2217), an IRRM2 engine run, or nothing —
 * a `SAVED` run (PROD-2742) is Save Run's record of the staged files, kept without generating, so the work can be
 * reloaded later. It has input files and a Reload like a workbook run, and no workbook name.
 */
export type EmpyreanRunKind = 'WORKBOOK' | 'IRRM2' | 'SAVED';

export const EMPYREAN_RUN_KINDS: readonly EmpyreanRunKind[] = ['WORKBOOK', 'IRRM2', 'SAVED'];

/**
 * Where the engine side of a run is.
 * - `UPLOADING` — the browser is still sending the payload.
 * - `SUBMITTING` — the payload is stored and checked; the API is bootstrapping curves and posting the job.
 * - `WAITING` — the engine has the job. It reports only pending / done / error: no progress, no ETA.
 * - `COLLECTING` — done; the API is streaming the result into Blob.
 * - `COMPLETE` — the result is stored.
 * - `FAILED` — the engine said error, or refused the submit (413, 422, a 504 that may have landed).
 * - `LOST` — the engine no longer knows the job (404: unknown or expired on AKS's durable store; restarted or
 *   evicted on an engine without it). Resubmit is safe.
 */
export type EmpyreanEngineStatus =
  | 'UPLOADING'
  | 'SUBMITTING'
  | 'WAITING'
  | 'COLLECTING'
  | 'COMPLETE'
  | 'FAILED'
  | 'LOST';

export const EMPYREAN_ENGINE_STATUSES: readonly EmpyreanEngineStatus[] = [
  'UPLOADING',
  'SUBMITTING',
  'WAITING',
  'COLLECTING',
  'COMPLETE',
  'FAILED',
  'LOST'
];

/** The statuses the leased poller works: everything after the upload and before an end. */
export const EMPYREAN_ENGINE_OPEN_STATUSES: readonly EmpyreanEngineStatus[] = ['SUBMITTING', 'WAITING', 'COLLECTING'];

export interface EmpyreanEngineRun {
  runId: string;
  status: EmpyreanEngineStatus;
  /** 1 for the first send; a resubmit is attempt n+1 of the same stored payload */
  attempt: number;
  /** the engine's `{epoch8}.{uuid4}`; null until the engine accepts the job */
  engineJobId: string | null;
  engineVersion: string | null;
  /** behaviour can change without a version bump, so the build is kept beside it */
  engineBuildId: string | null;
  /** UTF-8 bytes of the stored envelope, uncompressed */
  payloadBytes: number | null;
  instrumentCount: number | null;
  scenarioCount: number | null;
  /** ISO instants, UTC */
  submittedAtUtc: string | null;
  completedAtUtc: string | null;
  lastPolledAtUtc: string | null;
  /** the result's bytes as the engine sent them */
  resultBytes: number | null;
  /** the gzip the API stored */
  resultStoredBytes: number | null;
  /** the engine's code (`API.JOB.FAILED`, `API.REQUEST.TOO_LARGE`) or Emport's (`EMPORT.JOB_LOST`) */
  errorCode: string | null;
  errorMessage: string | null;
  /** the signed-in address of the analyst who last resubmitted it (PROD-2335); null if it never was */
  resubmittedBy: string | null;
  /** LOST or FAILED, with a stored payload: the API can send it again with no browser */
  canResubmit: boolean;
}

/** Whether IRRM2 is usable from this environment — what decides if Generate offers it at all (phase 4). */
export interface EmpyreanIrrm2Readiness {
  /** false when `EMPORT_IRRM2_BASE_URL` / `_API_KEY` are unset here: no IRRM2 choice, no poller */
  configured: boolean;
  /** configured, and `/health` answered within 5 s */
  reachable: boolean;
  /** `EMPORT_IRRM2_MAX_REQUEST_BYTES`, default 5,000,000 — the engine's own cap */
  maxRequestBytes: number;
  engineVersion: string | null;
  engineBuildId: string | null;
  /** why not, in words an analyst can act on; never a URL or a key */
  reason: string | null;
}

/** Which of a run's two stored blobs a download reads (PROD-2218): the envelope sent, or the result kept. */
export type EmpyreanEngineRunFile = 'PAYLOAD' | 'RESULT';

export const EMPYREAN_ENGINE_RUN_FILES: readonly EmpyreanEngineRunFile[] = ['PAYLOAD', 'RESULT'];

/**
 * One chunk of a run's stored payload or result, base64 (PROD-2218): what **Download payload** and
 * **Download result** fetch, the mirror of the payload upload. Each chunk says how many there are, so the
 * browser needs no size of its own to read the whole blob.
 */
export interface EmpyreanEngineRunFileChunk {
  runId: string;
  file: EmpyreanEngineRunFile;
  sequence: number;
  /** how many chunks the whole blob is read in */
  chunkCount: number;
  /** the stored blob's bytes: the payload uncompressed, the result as the gzip the API stored */
  totalBytes: number;
  /** what the browser saves it as: `5KIR_2026-06-30_irrm2-payload.json`, `5KIR_2026-06-30_irrm2-result-1.json.gz` */
  fileName: string;
  /** this chunk's bytes, base64 */
  data: string;
}

/**
 * One scenario of a COMPLETE run's results (PROD-2219, phase 5's screen), base first. Every figure is the engine's
 * own number from `emp.EmpyreanEngineRunMetrics`, except the 12-month NII and its change: a run goes at
 * `horizon_months: 24` (best guess, Luke's Decision 6), so the 12-month figures are the first 12 months of the
 * engine's `nii_by_month`, summed. `null` is "the engine reported none" — a ramp values no EVE — never zero.
 */
export interface EmpyreanEngineRunResultRow {
  /** 0 = the engine's base case; 1..n = its scenarios in the engine's order */
  sortOrder: number;
  isBase: boolean;
  scenarioId: string;
  /** parallel | ramp | non_parallel | basis; null for the base */
  scenarioType: string | null;
  /** nii | eve | both: which families the engine computed */
  appliesTo: string;
  eve: number | null;
  deltaEve: number | null;
  /** a fraction: -0.0367 is -3.67% */
  pctDeltaEve: number | null;
  /** NII over months 1-12: the sum of the engine's `nii_by_month` */
  nii12: number | null;
  deltaNii12: number | null;
  /** a fraction: ΔNII 12 / |base NII 12|, the engine's own convention for its horizon figure */
  pctDeltaNii12: number | null;
  /** NII over the run's whole horizon, cumulative — the engine's `total_nii` */
  niiHorizon: number | null;
  deltaNiiHorizon: number | null;
  /** a fraction — the engine's `pct_delta_nii` */
  pctDeltaNiiHorizon: number | null;
  /** sensitivities OF EQUITY — on the base row, from the base-only job (best guess, Luke's two-job split) */
  effectiveDuration: number | null;
  effectiveConvexity: number | null;
  dv01: number | null;
  findingCount: number;
}

/** Where a run's base duration family came from. */
export type EmpyreanEngineRunDurationSource = 'BASE_ONLY_JOB' | 'LADDER' | 'NONE';

export const EMPYREAN_ENGINE_RUN_DURATION_SOURCES: readonly EmpyreanEngineRunDurationSource[] = [
  'BASE_ONLY_JOB',
  'LADDER',
  'NONE'
];

/** The engine's findings (or its coverage report) for one code and severity: a count and the first message. */
export interface EmpyreanEngineRunFindingGroup {
  code: string;
  severity: string;
  count: number;
  example: string | null;
}

/** A COMPLETE run's results, as the results screen reads them (PROD-2219). */
export interface EmpyreanEngineRunResults {
  runId: string;
  portfolioCode: string;
  /** yyyy-MM-dd */
  asOfDate: string;
  engineVersion: string | null;
  engineBuildId: string | null;
  /** the run's NII horizon in months — 24 since the best-guess run options, 12 before */
  horizonMonths: number | null;
  /** base first; empty when nothing is stored, and `problem` says why */
  rows: EmpyreanEngineRunResultRow[];
  durationSource: EmpyreanEngineRunDurationSource;
  /** why the base row has no duration family, where it has none */
  durationProblem: string | null;
  findingTotal: number;
  findingGroups: EmpyreanEngineRunFindingGroup[];
  coverageTotal: number;
  coverageGroups: EmpyreanEngineRunFindingGroup[];
  /** why there are no figures: the run is not complete, or its result could not be read */
  problem: string | null;
}

/** What the browser says about a payload before it sends the bytes. The API checks every number again. */
export interface EmpyreanEngineRunBeginInput {
  portfolioCode: string;
  processorType: string;
  /** yyyy-MM-dd */
  asOfDate: string;
  includedDataTypes: string[];
  /** the dialog's options, JSON, as workbook runs record them */
  generateOptions: string;
  /** uncompressed; refused here when over `maxRequestBytes` */
  payloadBytes: number;
  /** lower-case hex */
  payloadSha256: string;
  instrumentCount: number;
  scenarioCount: number;
}

/**
 * The house policy limits, as percentages (Luke's `defaults/bank.json:2622-2633`: EaR 15, EVE 20; Kirkwood,
 * BankFirst and 1st State all carry exactly these). There are no per-client limits yet — Luke's open question
 * is whether the 2026 Initial Assumptions sheet's per-rung table should replace them.
 *
 * Here, not in the results screen's lib, so the screen and the IRRM2 report's digest (PROD-2383) read one constant:
 * a run the engine sent no limits for is judged against these on both.
 */
export const IRRM2_HOUSE_LIMITS = { earPct: 15, evePct: 20 } as const;

/**
 * A COMPLETE IRRM2 run's IRRM Executive Summary (PROD-2390's `empyreanIrrm2Report`), as **View Report** reads it
 * (PROD-2391): the self-contained document, and its name, which is also its `<title>`. The query has more (when the
 * digest was built, the spec §12 checks); the browser asks only for these.
 */
export interface EmpyreanIrrm2Report {
  html: string;
  /** `IRRM Executive Summary - 5KIR - 2026-06-30 - DRAFT` */
  fileName: string;
}

/** The same report as a PDF (PROD-2392's `empyreanIrrm2ReportPdf`), as **Download PDF** saves it. */
export interface EmpyreanIrrm2ReportPdf {
  /** the file, base64 */
  base64: string;
  /** `IRRM Executive Summary - 5KIR - 2026-06-30 - DRAFT.pdf` */
  fileName: string;
}
